/**
 * ImportExcelCirurgiasModal - Modal para importar Excel de Cirurgias a Faturar
 */
import React from 'react';
import { X, Upload, FileText, AlertCircle, Stethoscope, ClipboardPaste } from 'lucide-react';
import type { Cliente } from '../../services/clienteService';

interface ImportExcelCirurgiasModalProps {
  error: string | null;
  selectedFile: File | null;
  uploading: boolean;
  uploadResult: any;
  jobClientes: Cliente[];
  selectedImportClient: number | null;
  selectedRegime: boolean | null;
  setSelectedFile: (file: File | null) => void;
  setSelectedImportClient: (id: number | null) => void;
  setSelectedRegime: (regime: boolean | null) => void;
  handleUploadExcel: () => void;
  closeImportModal: () => void;
  setError: (error: string | null) => void;
  // T26 (#15): Colar do Excel
  importMode: 'arquivo' | 'colar';
  setImportMode: (mode: 'arquivo' | 'colar') => void;
  pastedText: string;
  setPastedText: (text: string) => void;
  pastedRows: number;
}

export const ImportExcelCirurgiasModal: React.FC<ImportExcelCirurgiasModalProps> = ({
  error,
  selectedFile,
  uploading,
  uploadResult,
  jobClientes,
  selectedImportClient,
  selectedRegime,
  setSelectedFile,
  setSelectedImportClient,
  setSelectedRegime,
  handleUploadExcel,
  closeImportModal,
  setError,
  importMode,
  setImportMode,
  pastedText,
  setPastedText,
  pastedRows,
}) => {
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError('Arquivo deve ser Excel (.xlsx ou .xls)');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setSelectedImportClient(null);
    setSelectedRegime(null);
    closeImportModal();
  };

  // Determinar cliente ativo para lógica de regime
  const getActiveClient = (): Cliente | null => {
    if (jobClientes.length === 1) {
      return jobClientes[0];
    } else if (selectedImportClient) {
      return jobClientes.find(c => c.id === selectedImportClient) || null;
    }
    return null;
  };

  const activeClient = getActiveClient();
  const needsRegimeSelection = activeClient && activeClient.com_regime && activeClient.sem_regime;

  // Auto-select regime for single-regime clients (mirrors useAddItemForm.ts:107-114)
  React.useEffect(() => {
    const client = getActiveClient();
    if (client) {
      if (client.com_regime && !client.sem_regime) {
        setSelectedRegime(true);
      } else if (!client.com_regime && client.sem_regime) {
        setSelectedRegime(false);
      }
      // If both regimes: leave null, selector will appear
    }
  }, [jobClientes, selectedImportClient]);

  // Validar se pode fazer upload
  const canUpload = () => {
    if (uploading) return false;
    // T26: no modo colar basta ter linhas detectadas (header + dados)
    if (importMode === 'colar' ? pastedRows < 2 : !selectedFile) return false;
    if (jobClientes.length > 1 && !selectedImportClient) return false;
    if (needsRegimeSelection && selectedRegime === null) return false;
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full">
        {/* Header - Green for Cirurgias */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Stethoscope size={24} />
            Importar Excel - Cirurgias a Faturar
          </h2>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Client selection */}
          {jobClientes.length === 1 ? (
            // Um único cliente - mostrar como texto
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Cliente:
              </label>
              <div className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white font-medium">
                {jobClientes[0].nome_fantasia || jobClientes[0].nome}
              </div>
              {/* Show auto-selected regime info */}
              {activeClient && !needsRegimeSelection && (activeClient.com_regime || activeClient.sem_regime) && (
                <div className="mt-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                  Regime Especial: <strong>{activeClient.com_regime ? 'Com Regime' : 'Sem Regime'}</strong>
                </div>
              )}
            </div>
          ) : jobClientes.length > 1 ? (
            // Múltiplos clientes - dropdown
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Selecione o Cliente para as cirurgias:
              </label>
              <select
                value={selectedImportClient || ''}
                onChange={(e) => {
                  const cid = e.target.value ? Number(e.target.value) : null;
                  setSelectedImportClient(cid);
                  setSelectedRegime(null); // Reset regime ao mudar cliente

                  // Auto-select regime se cliente tem apenas um tipo
                  if (cid) {
                    const client = jobClientes.find(c => c.id === cid);
                    if (client) {
                      if (client.com_regime && !client.sem_regime) {
                        setSelectedRegime(true);
                      } else if (!client.com_regime && client.sem_regime) {
                        setSelectedRegime(false);
                      }
                    }
                  }
                }}
                className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Selecione um cliente...</option>
                {jobClientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome_fantasia || cliente.razao_social}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Seletor de Regime - Só aparece se cliente tem AMBOS os regimes */}
          {needsRegimeSelection && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Regime Especial:
              </label>
              <select
                value={selectedRegime === null ? '' : (selectedRegime ? 'true' : 'false')}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedRegime(val === '' ? null : val === 'true');
                }}
                className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Selecione o regime...</option>
                <option value="true">Com Regime</option>
                <option value="false">Sem Regime</option>
              </select>
            </div>
          )}

          {/* REPLACE warning */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-3 rounded">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              <strong>⚠ Atenção:</strong> Ao importar, as cirurgias anteriores serão substituídas.
            </p>
          </div>

          {/* T26 (#15): alternar entre Arquivo e Colar do Excel */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setImportMode('arquivo')}
              disabled={uploading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${importMode === 'arquivo' ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <Upload size={16} /> Arquivo Excel
            </button>
            <button
              type="button"
              onClick={() => setImportMode('colar')}
              disabled={uploading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${importMode === 'colar' ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <ClipboardPaste size={16} /> Colar do Excel
            </button>
          </div>

          {importMode === 'arquivo' ? (
            /* File Input */
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Selecione o arquivo Excel:
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="flex-1 text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 dark:file:bg-green-900/20 file:text-green-700 dark:file:text-green-400 hover:file:bg-green-100 dark:hover:file:bg-green-900/30 disabled:opacity-50 cursor-pointer"
                />
                {selectedFile && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <FileText size={16} />
                    <span className="font-medium">{selectedFile.name}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* T26: Colar do Excel (TSV). Diferente da base, o import de
               cirurgias identifica as colunas pelo CABEÇALHO — a 1ª linha
               colada PRECISA ser o cabeçalho. */
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Selecione as colunas no Excel <strong>incluindo o cabeçalho</strong>, copie (Ctrl+C) e cole abaixo (Ctrl+V):
              </label>
              <textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                disabled={uploading}
                rows={10}
                placeholder="Cole aqui as células copiadas do Excel (com a linha de cabeçalho: DATA/HORA, PACIENTE, MEDICO, CONVENIO, CODIGO PRODUTO, LOTE, QUANTIDADE, CNPJ)..."
                className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              />
              {pastedRows > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {pastedRows} linha(s) detectada(s) — a 1ª deve ser o cabeçalho.
                </p>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-400">
                  Erro na Importação
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Lista de Erros Detalhados (Epic 43) */}
          {uploadResult?.erros && uploadResult.erros.length > 0 && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 rounded">
              <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-400 mb-2">
                Erros encontrados ({uploadResult.total_erros || uploadResult.erros.length} no total):
              </h4>
              <ul className="text-sm text-orange-700 dark:text-orange-300 space-y-1 max-h-40 overflow-y-auto">
                {uploadResult.erros.map((err: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-orange-500 mt-0.5">•</span>
                    <span>{err}</span>
                  </li>
                ))}
              </ul>
              {uploadResult.total_erros && uploadResult.total_erros > 10 && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 italic">
                  ...e mais {uploadResult.total_erros - 10} erro(s)
                </p>
              )}
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  Enviando arquivo e processando cirurgias...
                </span>
              </div>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && !uploading && (
            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded">
              <p className="text-sm text-green-700 dark:text-green-300">
                Upload concluido! {uploadResult.total_processadas} de {uploadResult.total_linhas} linhas processadas.
              </p>
              {uploadResult.total_erros > 0 && (
                <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                  {uploadResult.total_erros} linhas com erro.
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleClose}
              disabled={uploading}
              className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleUploadExcel}
              disabled={!canUpload()}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={18} />
              {uploading ? 'Enviando...' : 'Importar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
